import { defineStore, storeToRefs } from "pinia";
import axios from "axios";
import { FullReport } from "../components/ContactsEditor/schema";
import { FetchValidationMessagesResponse, SelectItem, UploadType, UploadedFileDetails } from "@/types";
import { handleErrorInResponse, addIdPropertyToTargetArrays } from "@/utils";
import { BFilter, BFilterableField } from "@/components/BFilterEditor/BFilterEditor.types";
import { BSortOptions, BTableColumn, BTableOptions, serializeSortBy } from "@/components/BTable/BTable.types";
import { defaultDataUploadDetailsColumns, defaultStatisticsColumns, defaultValidationMessagesColumns, defaultValidationMessagesFilterableFields } from "@/tableDefaults";
import { ref, computed } from "vue";
import { useBaseStore } from "./baseStore";
import { useOriStore } from "./oriStore";
import { CodeLabelMapByKind, CodeLabelMap, IncommingDataForSelects } from "./CodeLabelMapByKind";

export const useContactsStore = defineStore("contactsStore", () => {
    var baseStore = useBaseStore();
    const apiUrl = "api/v1/contacts";
    baseStore.apiUrl.value = apiUrl;
    baseStore.dataset.value = UploadType.contacts;

    var oriStore = useOriStore();
    oriStore.reset();
    oriStore.dataset = UploadType.contacts;


    var ages = ref([] as number[]);
    var contactFullReport = ref({} as FullReport | undefined);
    var codeLabelByKind = ref([] as CodeLabelMapByKind[]);

    var getStatisticsColumns = computed(() => {
        return defaultStatisticsColumns;
    });

    var getColumns = computed(() => {
        return defaultDataUploadDetailsColumns;
    });

    var getFilterableFields = computed(() => {
        return baseStore.getDefaultFilterableFields;
    });

    var getValidationMessagesColumns = computed(() => {
        return defaultValidationMessagesColumns;
    });

    var getValidationMessagesFilterableFields = computed(() => {
        return defaultValidationMessagesFilterableFields;
    });

    var showWarningDetails = computed(() => {
        return true;
    });

    var errorCodes = computed(() => {
        return baseStore.errorCodes;
    });

    var errorDescriptions = computed(() => {
        return baseStore.errorDescriptions;
    });

    var getCodeLabels = computed(() => {
        return (kind: string) =>
            codeLabelByKind.value.find((f) => f.kind == kind)?.codes;
    });

    var getGender = computed(() => {
        let filterList = getCodeLabels.value;
        const genders = filterList("Gender") as CodeLabelMap[];

        return genders?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getEthnicity = computed(() => {
        let filterList = getCodeLabels.value;
        const ethnicity = filterList("Ethnicity") as CodeLabelMap[];

        return ethnicity?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getRace = computed(() => {
        let filterList = getCodeLabels.value;
        const race = filterList("Race") as CodeLabelMap[];

        return race?.map((m) => ({ label: m.label, value: m.code } as SelectItem));
    });

    var getContactReason = computed(() => {
        let filterList = getCodeLabels.value;
        const contactReason = filterList("ContactReason") as CodeLabelMap[];

        return contactReason?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getCrimeCategorySuspected = computed(() => {
        let filterList = getCodeLabels.value;
        const crimeCategorySuspected = filterList(
            "CrimeCategorySuspected"
        ) as CodeLabelMap[];

        return crimeCategorySuspected?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getCrimeCategoryCommitted = computed(() => {
        let filterList = getCodeLabels.value;
        const crimeCategoryCommitted = filterList(
            "CrimeCategoryCommitted"
        ) as CodeLabelMap[];

        return crimeCategoryCommitted?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getInformationCollected = computed(() => {
        let filterList = getCodeLabels.value;
        const informationCollected = filterList(
            "InformationCollected"
        ) as CodeLabelMap[];

        return informationCollected?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getInitiationType = computed(() => {
        let filterList = getCodeLabels.value;
        const initiationType = filterList("InitiationType") as CodeLabelMap[];

        return initiationType?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getLocationType = computed(() => {
        let filterList = getCodeLabels.value;
        const locationCategory = filterList("LocationCategory") as CodeLabelMap[];

        return locationCategory?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getInvestigationOutcome = computed(() => {
        let filterList = getCodeLabels.value;
        const investigationOutcome = filterList(
            "InvestigationOutcome"
        ) as CodeLabelMap[];

        return investigationOutcome?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getComplaintOutcome = computed(() => {
        let filterList = getCodeLabels.value;
        const complaintOutcome = filterList("ComplaintOutcome") as CodeLabelMap[];

        return complaintOutcome?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getContactOutcome = computed(() => {
        let filterList = getCodeLabels.value;
        const contactOutcome = filterList("ContactOutcome") as CodeLabelMap[];

        return contactOutcome?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getError = computed(() => {
        let filterList = getCodeLabels.value;
        const error = filterList("Error") as CodeLabelMap[];

        return error?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getInjurySeverity = computed(() => {
        let filterList = getCodeLabels.value;
        const injurySeverity = filterList("InjurySeverity") as CodeLabelMap[];

        return injurySeverity?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getLocationCategory = computed(() => {
        let filterList = getCodeLabels.value;
        const locationCategory = filterList("LocationCategory") as CodeLabelMap[];

        return locationCategory?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getOfficerTypeOfForce = computed(() => {
        let filterList = getCodeLabels.value;
        const officerTypeOfForce = filterList(
            "OfficerTypeOfForce"
        ) as CodeLabelMap[];

        return officerTypeOfForce?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getOfficerWeaponType = computed(() => {
        let filterList = getCodeLabels.value;
        const officerWeaponType = filterList(
            "OfficerWeaponType"
        ) as CodeLabelMap[];

        return officerWeaponType?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getPropertySeizureBasis = computed(() => {
        let filterList = getCodeLabels.value;
        const propertySeizureBasis = filterList(
            "PropertySeizureBasis"
        ) as CodeLabelMap[];

        return propertySeizureBasis?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getPropertyType = computed(() => {
        let filterList = getCodeLabels.value;
        const propertyType = filterList("PropertyType") as CodeLabelMap[];

        return propertyType?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getSearchBasis = computed(() => {
        let filterList = getCodeLabels.value;
        const searchBasis = filterList("SearchBasis") as CodeLabelMap[];

        return searchBasis?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getShowUpIdOutcome = computed(() => {
        let filterList = getCodeLabels.value;
        const showUpIdOutcome = filterList("ShowUpIdOutcome") as CodeLabelMap[];

        return showUpIdOutcome?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getSubjectWeaponType = computed(() => {
        let filterList = getCodeLabels.value;
        const subjectWeaponType = filterList(
            "SubjectWeaponType"
        ) as CodeLabelMap[];

        return subjectWeaponType?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getWhatSearched = computed(() => {
        let filterList = getCodeLabels.value;
        const whatSearched = filterList("WhatSearched") as CodeLabelMap[];

        return whatSearched?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getYesNoUnknown = computed(() => {
        let filterList = getCodeLabels.value;
        const yesNoUnknown = filterList("YesNoUnknown") as CodeLabelMap[];

        return yesNoUnknown?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getYesNo = computed(() => {
        return [{ label: 'Yes', value: true }, { label: 'No', value: false }] as SelectItem[];
    });

    var getWeaponDischarged = computed(() => {
        let items = [{ label: 'Weapon Discharged', value: 'true' }, { label: 'Weapon Not Discharged', value: 'false' }, { label: 'Not applicable', value: -99 }] as SelectItem[];

        return items;
    });

    function fetchAges() {
        try {
            let data = [] as number[];
            for (let i = 1; i < 100; i++) {
                data.push(i);
            }
            ages.value = data;
        } catch (error) {
            console.error(error);
        }
    }

    function fetchContacts(incidentNumber: string, ori: string) {
        var fullUrl = baseStore.apiUrl.value;
        fullUrl += `/incident-number/${incidentNumber}/ori/${ori}`;
        return axios.get<FullReport>(fullUrl);
    }

    async function fetchCodeLabelByKind() {
        try {
            var fullUrl = baseStore.apiUrl.value;
            fullUrl += `/code-label-map`;

            const data = await axios.get<IncommingDataForSelects[]>(fullUrl);
            if (data.data) {
                data.data.forEach((f) => {
                    if (codeLabelByKind.value.filter((x) => x.kind == f.kind).length === 0) {
                        let codeLabelMap = data.data
                            .filter((x) => x.kind == f.kind)
                            ?.map((m) => ({ code: m.code, label: m.label } as CodeLabelMap));
                        codeLabelByKind.value.push({ kind: f.kind, codes: codeLabelMap });
                    }
                });
            }
        } catch (error) {
            handleErrorInResponse(error);
        }
    }

    function removeInvestigation(index: number) {

        if (contactFullReport.value != undefined && contactFullReport.value?.investigation_outcome != undefined && index >= 0 && index < contactFullReport.value!.investigation_outcome!.length - 1) {
            contactFullReport.value!.investigation_outcome!.splice(index, 1);
        }
    }

    function getValidationMessagesForFile(id: number, pageNumber: number, pageSize: number, filterInfo: BFilter[], filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        return baseStore.getValidationMessagesForFile(id, pageNumber, pageSize, filterInfo, filterableFields, sortingInfo);
    }

    function getRecordDetails(id: number) {
        return baseStore.getRecordDetails(id);
    }

    function getErrorsAndWarnings(filters: BFilter[], tableOptions: BTableOptions, filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        return baseStore.getErrorsAndWarnings(tableOptions, filters, filterableFields)
    }

    function getErrorsAndWarningsForFile(id: number, filters: BFilter[], tableOptions: BTableOptions, filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        return baseStore.getErrorsAndWarningsForUploadDetails(id, tableOptions, filters, filterableFields)
    }

    var getErrorsAndWarningsColumns = baseStore.getErrorsAndWarningsColumns;
    function getFilterableFieldsForErrorsAndWarnings(onlyHasOneOri: boolean, showAdditionalColumns: boolean, onlyIncludePrimaryColumns: boolean) {

        var ewFilters = baseStore.getFilterableFieldsForErrorsAndWarnings(onlyHasOneOri, showAdditionalColumns, onlyIncludePrimaryColumns);

        var agencyFilter = ewFilters.find(f => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return ewFilters;
    }

    function getFiles(filters: BFilter[], tableOptions: BTableOptions, filterableFields: BFilterableField[], sortingInfo: BSortOptions[]) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getFiles(tableOptions, filters ?? [], filterableFields)
    }
    var getFileUploadColumns = baseStore.getFileUploadColumns;
    function getFilterableFieldsForFileUploads(onlyHasOneOri: boolean, submissionPeriodHidden: boolean) {
        var filterableFields = baseStore.getFilterableFieldsForFileUploads(onlyHasOneOri, submissionPeriodHidden);

        var agencyFilter = filterableFields.find(f => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return filterableFields;
    }
    var errorDescriptionsDownloadUrl = ref(baseStore.errorDescriptionsDownloadUrl);

    //ori related functions & computed properties        
    const { oris, getOriName, getOrisForSelectList, getOrisAsBFieldSelectionArray, getDetailsForCurrentDataset, getSelectedOri } = storeToRefs(oriStore)
    var fetchDatasetDetailsForOri = oriStore.fetchDatasetDetailsForOri;
    var reset = oriStore.reset;
    var setSelectedOri = oriStore.setSelectedOri;
    var checkIfDownloadValid = baseStore.checkIfDownloadValid;


    var loadErrorsSummary = baseStore.loadErrorsSummary;
    var datasetSummary = baseStore.datasetSummary;
    var loadStatisticsSummary = baseStore.loadStatisticsSummary;
    var datasetStatisticsSummary = baseStore.datasetStatisticsSummary;
    var loadLastSubmission = baseStore.loadLastSubmission;
    var datasetLastSubmissions = baseStore.datasetLastSubmissions;

    return {
        ages, oris, contactFullReport, errorCodes, errorDescriptions, getStatisticsColumns, getColumns, getValidationMessagesColumns, getValidationMessagesFilterableFields, showWarningDetails, getCodeLabels, getGender, getEthnicity,
        getRace, getContactReason, getCrimeCategorySuspected, getCrimeCategoryCommitted, getInformationCollected, getInitiationType, getLocationType, getInvestigationOutcome,
        getComplaintOutcome, getContactOutcome, getError, getInjurySeverity, getLocationCategory, getOfficerTypeOfForce, getOfficerWeaponType, getPropertySeizureBasis,
        getPropertyType, getSearchBasis, getShowUpIdOutcome, getSubjectWeaponType, getWhatSearched, getYesNoUnknown, getYesNo, getWeaponDischarged, fetchAges, fetchContacts, getFiles,
        getFilterableFields, fetchCodeLabelByKind, removeInvestigation, getValidationMessagesForFile, getRecordDetails, getErrorsAndWarnings, getErrorsAndWarningsForFile,
        getOriName, getOrisForSelectList, getOrisAsBFieldSelectionArray, getDetailsForCurrentDataset, fetchDatasetDetailsForOri, reset, getSelectedOri, setSelectedOri,
        errorDescriptionsDownloadUrl, getFileUploadColumns, getFilterableFieldsForFileUploads, getErrorsAndWarningsColumns, getFilterableFieldsForErrorsAndWarnings, checkIfDownloadValid,
        loadErrorsSummary, datasetSummary, loadStatisticsSummary, datasetStatisticsSummary, loadLastSubmission, datasetLastSubmissions, apiUrl
    }
});
